import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import type { LWApiPublicBillingPaymentInstrumentDetailsRaw } from '@liquidweb-dev/club-sauce/public/billing/payment/instrument/index.raw';

// Expiry formats are matched exactly rather than guessed at, which dayjs cannot do
// without this plugin. Registering it here rather than at a call site: this module is
// the only place these formats are read.
dayjs.extend(customParseFormat);

/**
 * Every shape an expiry can arrive in, tried in order.
 *
 * Stripe's `card_exp` is always `MM/YYYY`. The Authorize.Net bag's `credit_card_exp` is
 * not: its column validates as `DATE_VALID|DATETIME|MONTH_YEAR`, so the same field can
 * hold a bare month/year, a date, or a datetime depending on which path wrote it. Parsing
 * is strict, so an unrecognised value resolves to *unknown* rather than to a wrong date.
 *
 * Each shape is listed twice, zero-padded and not. The server's three validators all
 * accept single digits — `MONTH_YEAR` is `^\d{1,2}/\d{4}\z`, `DATE_VALID` is
 * `^\d{4}-\d{1,2}-\d{1,2}$`, and `TIMESTAMP_VALID` allows single digits throughout and
 * makes seconds optional (`LW/Input/Validator/date.pm`). dayjs in strict mode treats the
 * padded and unpadded tokens as mutually exclusive, so `MM` will not match `2` and `M`
 * will not match `02`; covering both needs both. Listing only the padded forms leaves a
 * genuinely expired legacy card resolving to unknown, showing no banner at all.
 */
const EXPIRY_FORMATS = [
	'MM/YYYY',
	'M/YYYY',
	'YYYY-MM-DD HH:mm:ss',
	'YYYY-M-D H:m:s',
	'YYYY-MM-DD HH:mm',
	'YYYY-M-D H:m',
	'YYYY-MM-DD',
	'YYYY-M-D',
];

/**
 * The shape every expiry is normalised to before it is handed back for display.
 *
 * Exported because `getPaymentMethodBanner` shortens the year for the row label and so
 * depends on this being `MM/YYYY`. A test there pins the pairing, which is otherwise an
 * invisible coupling between two files.
 */
export const EXPIRY_DISPLAY_FORMAT = 'MM/YYYY';

/** The attention state of a single payment method. Deliberately one value, never a set. */
export type PaymentMethodStatusT =
	| 'declined'
	| 'expired'
	| 'expiringSoon'
	| 'failed'
	| 'ok';

/** The states a transaction outcome can resolve to, as opposed to an expiry-derived one. */
type TransactionDerivedStatusT = Extract<
	PaymentMethodStatusT,
	'declined' | 'failed'
>;

/**
 * The attention state each `lastTransactionStatus` value maps to.
 *
 * Exhaustive over the field's enum rather than a list of the interesting values, so a
 * value the server adds later fails the type check here instead of silently resolving to
 * a healthy method.
 *
 * `declined` and `failed` are distinct because the server distinguishes them, but they
 * are separate causes rather than degrees: `error` is the server's catch-all, covering
 * processing failures, unmapped decline codes, invalid card data, and failed 3DS
 * authentication alike. It carries no recoverable reason — the underlying code is logged
 * server-side and is not on the instrument — so `failed` copy cannot name a cause or
 * promise the customer either that they can fix it or that they cannot.
 */
const TRANSACTION_STATUS_MAP: Record<
	NonNullable<
		LWApiPublicBillingPaymentInstrumentDetailsRaw['lastTransactionStatus']
	>,
	TransactionDerivedStatusT | undefined
> = {
	declined: 'declined',
	error: 'failed',
	successful: undefined,
};

export interface PaymentMethodStatusI {
	/** The recorded expiry in `MM/YYYY`, when the method carries a readable one. */
	expiresAt?: string;
	status: PaymentMethodStatusT;
}

export interface GetPaymentMethodStatusParamsI {
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw;
	/** Injected so expiry-boundary behaviour is testable without faking timers. */
	now?: Dayjs;
}

/**
 * The raw expiry a method carries, whichever key its processor writes it under.
 *
 * Stripe keeps it in `card_exp`. Authorize.Net keeps it in `credit_card_exp`, and on rows
 * written by the migration also in `expirationDateForDisplay` — which is preferred where
 * present because it is unambiguously `MM/YYYY`, while `credit_card_exp` is not.
 *
 * Bank, PayPal, and manual instruments carry no expiry at all: the Authorize.Net write
 * path explicitly drops `credit_card_exp` for an ACH profile as meaningless, and the other
 * two processors have no card behind them.
 */
const getRawExpiry = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): string | undefined => {
	// Preferred over `metadata` where present. Parsed against the same `EXPIRY_FORMATS`
	// as the metadata shapes rather than assumed to be `MM/YYYY`, so an unexpected shape
	// resolves to unknown rather than to a wrong date.
	if (instrument.cardDetails?.expiration) {
		return instrument.cardDetails.expiration;
	}

	if (instrument.processor === 'stripe') {
		return instrument.metadata.card_exp;
	}

	if (instrument.processor === 'authorize_net') {
		return (
			instrument.metadata.expirationDateForDisplay ??
			instrument.metadata.credit_card_exp
		);
	}

	return undefined;
};

/**
 * Parses an expiry into the last moment it is still valid — a card expires at the end of
 * its stated month, not at the start.
 *
 * Returns `undefined` for anything that matches no known format, so an unreadable value
 * is treated as *unknown* rather than as expired.
 */
const getEndOfExpiryMonth = (rawExpiry?: string): Dayjs | undefined => {
	if (!rawExpiry) {
		return undefined;
	}

	const parsed = EXPIRY_FORMATS.map((format) =>
		dayjs(rawExpiry, format, true),
	).find((candidate) => candidate.isValid());

	return parsed?.endOf('month');
};

/** The parsed end-of-month and the `MM/YYYY` display form, when the expiry is readable. */
const resolveExpiry = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
) => {
	const endOfExpiryMonth = getEndOfExpiryMonth(getRawExpiry(instrument));

	return {
		endOfExpiryMonth,
		expiresAt: endOfExpiryMonth?.format(EXPIRY_DISPLAY_FORMAT),
	};
};

/**
 * The recorded expiry in `MM/YYYY`, when the method carries a readable one.
 *
 * Normalised on the way out, so a caller renders one shape whatever the processor
 * wrote — a legacy row's `YYYY-MM-DD` would otherwise reach the UI verbatim.
 */
export const getExpiresAt = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): string | undefined => resolveExpiry(instrument).expiresAt;

/**
 * Reads the transaction status the `lastTransactionStatus` alsowith supplies.
 *
 * Absent means "no answer" — never "healthy" — so an instrument with no transaction
 * history falls through to the expiry branches rather than being treated as declined
 * or as cleared.
 */
const getLastTransactionStatus = (
	instrument: LWApiPublicBillingPaymentInstrumentDetailsRaw,
): TransactionDerivedStatusT | undefined => {
	const { lastTransactionStatus } = instrument;

	return lastTransactionStatus
		? TRANSACTION_STATUS_MAP[lastTransactionStatus]
		: undefined;
};

/**
 * Resolves one payment method to exactly one attention state.
 *
 * Precedence is expired, then the transaction outcome, then expiring soon. Expired
 * outranks a failed charge because it names the cause and its remedy supersedes it. Both
 * outrank expiring soon, which is a warning about the future rather than a failure that
 * has already happened.
 *
 * `declined` and `failed` never compete: both derive from the single
 * `lastTransactionStatus` value, so at most one can be set.
 *
 * Expiry and transaction status are orthogonal signals, not competing views of one
 * thing: `lastTransactionStatus` describes a charge outcome, so a card can be expired
 * while its last charge succeeded, and vice versa. There is therefore no
 * server-versus-client precedence rule to apply.
 */
const getPaymentMethodStatus = ({
	instrument,
	now = dayjs(),
}: GetPaymentMethodStatusParamsI): PaymentMethodStatusI => {
	const { endOfExpiryMonth, expiresAt } = resolveExpiry(instrument);

	const isExpired = Boolean(endOfExpiryMonth?.isBefore(now));

	// Guarded on `!isExpired` rather than relying on branch order: every already-expired
	// card is also "before one month from now", so the two overlap unless separated here.
	const isExpiringSoon =
		!isExpired && Boolean(endOfExpiryMonth?.isBefore(now.add(1, 'month')));

	const transactionStatus = getLastTransactionStatus(instrument);

	if (isExpired) {
		return { expiresAt, status: 'expired' };
	}

	if (transactionStatus) {
		return { expiresAt, status: transactionStatus };
	}

	if (isExpiringSoon) {
		return { expiresAt, status: 'expiringSoon' };
	}

	return { expiresAt, status: 'ok' };
};

export default getPaymentMethodStatus;
